import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Textarea } from '../ui'
import {
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  AlertTriangle,
  Clock,
  User,
  Calendar
} from 'lucide-react'
import { formatDate } from '../../utils/formatters'
import { useVerifyDocument, useRejectDocument, useDownloadDocument } from '../../hooks/useDocuments'
import { Document, DocumentStatus } from '../../types'

interface DocumentReviewPanelProps {
  document: Document
  onReviewComplete?: () => void
}

export default function DocumentReviewPanel({ document, onReviewComplete }: DocumentReviewPanelProps) {
  const [verificationNotes, setVerificationNotes] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const verifyDocument = useVerifyDocument()
  const rejectDocument = useRejectDocument()
  const downloadDocument = useDownloadDocument()

  const getStatusColor = (status: DocumentStatus): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'UPLOADED':
        return 'bg-blue-100 text-blue-800'
      case 'VERIFIED':
        return 'bg-green-100 text-green-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4" />
      case 'UPLOADED':
        return <FileText className="h-4 w-4" />
      case 'VERIFIED':
        return <CheckCircle className="h-4 w-4" />
      case 'REJECTED':
        return <XCircle className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  const getStatusText = (status: DocumentStatus): string => {
    switch (status) {
      case 'PENDING':
        return 'Pending Review'
      case 'UPLOADED':
        return 'Uploaded'
      case 'VERIFIED':
        return 'Verified'
      case 'REJECTED':
        return 'Rejected'
      default:
        return status
    }
  }

  const handleVerify = async () => {
    if (isProcessing) return

    setIsProcessing(true)
    try {
      await verifyDocument.mutateAsync({
        id: document.id,
        verificationNotes: verificationNotes || 'Document verified by admin'
      })
      setVerificationNotes('')
      onReviewComplete?.()
    } catch (error: any) {
      console.error('Verification error:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReject = async () => {
    if (isProcessing) return

    if (!verificationNotes.trim()) {
      alert('Please provide rejection reason')
      return
    }

    setIsProcessing(true)
    try {
      await rejectDocument.mutateAsync({
        id: document.id,
        verificationNotes
      })
      setVerificationNotes('')
      setShowRejectForm(false)
      onReviewComplete?.()
    } catch (error: any) {
      console.error('Rejection error:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDownload = async () => {
    try {
      await downloadDocument.mutateAsync({
        id: document.id,
        filename: document.originalName
      })
    } catch (error: any) {
      console.error('Download error:', error)
    }
  }

  const isAlreadyReviewed = document.status === 'VERIFIED' || document.status === 'REJECTED'

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg">{document.originalName}</CardTitle>
            <div className="flex items-center space-x-2 mt-2">
              <Badge className={getStatusColor(document.status)}>
                <div className="flex items-center space-x-1">
                  {getStatusIcon(document.status)}
                  <span>{getStatusText(document.status)}</span>
                </div>
              </Badge>
              <span className="text-sm text-neutral-500">
                {document.type.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={isProcessing}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Document Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-neutral-500">Uploaded:</span>
            <span className="ml-2 font-medium">
              {formatDate(document.uploadedAt)}
            </span>
          </div>
          <div>
            <span className="text-neutral-500">File Size:</span>
            <span className="ml-2 font-medium">
              {(document.fileSize / 1024).toFixed(1)} KB
            </span>
          </div>
          <div>
            <span className="text-500">Case:</span>
            <span className="ml-2 font-medium">
              {document.case?.caseNumber || 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-neutral-500">Family:</span>
            <span className="ml-2 font-medium">
              {document.case?.family?.firstName} {document.case?.family?.lastName}
            </span>
          </div>
        </div>

        {/* Verification Notes */}
        {document.verificationNotes && (
          <div className="p-3 bg-neutral-50 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-neutral-900">Previous Notes:</p>
                <p className="text-sm text-neutral-700 mt-1">{document.verificationNotes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Actions */}
        {!isAlreadyReviewed && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Review Notes (Required for rejection):
              </label>
              <Textarea
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="Enter review notes for this document..."
                rows={3}
                className="w-full"
              />
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectForm(true)}
                disabled={isProcessing}
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>

              <Button
                variant="islamic"
                onClick={handleVerify}
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? 'Processing...' : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Verify Document
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Rejection Confirmation */}
        {showRejectForm && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">
                  Confirm Document Rejection
                </p>
                <p className="text-sm text-red-700 mt-1">
                  This action will mark the document as rejected. The family will need to upload a new document.
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRejectForm(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleReject}
                disabled={isProcessing}
              >
                {isProcessing ? 'Rejecting...' : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Confirm Rejection
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Already Reviewed Status */}
        {isAlreadyReviewed && (
          <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  Document {document.status.toLowerCase()}
                </p>
                {document.verifiedAt && (
                  <p className="text-sm text-green-700">
                    Reviewed on {formatDate(document.verifiedAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}